import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Tooltip,
  Tabs,
} from 'antd'
import {
  CheckCircleOutlined,
  EditOutlined,
  FileSearchOutlined,
  SearchOutlined,
  FileTextOutlined,
  SwapOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { conversionFor } from './unit-conversion'
import { priceRounding } from './money-rounding'
import {
  displayValue,
  LineDraft,
  LineField,
  matchFor,
  moneyValue,
  ReviewLine,
  valuesFor,
} from './result-model'
import {
  canConfirmLineDraft,
  comparablePriceDifference,
  confirmedLineDraft,
  lineReviewLabels,
} from './line-review-model'
import { MatchMemoryControls } from './match-memory'
import { InvoiceField, InvoiceFieldsEditor } from '../invoice-fields'
import AdditionalFields from './additional-fields'

const mainFields: LineField[] = ['unit', 'quantity', 'price', 'amount']
const numbers: LineField[] = ['quantity', 'price', 'amount', 'vat_amount', 'total']

export default function LineReviewDialog({
  line,
  draft,
  disabled,
  allowUnlinked,
  amountBasis,
  definitions,
  hasDocumentContext,
  focusedField,
  originalAction,
  onChange,
  onChangeValue,
  onChooseProduct,
  onShowOriginal,
  onClose,
  onSave,
}: {
  line: ReviewLine | null
  draft: LineDraft | null
  disabled: boolean
  allowUnlinked: boolean
  amountBasis?: LineDraft['amount_basis']
  definitions: InvoiceField[]
  hasDocumentContext: boolean
  focusedField?: LineField
  originalAction?: React.ReactNode
  onChange: (value: LineDraft) => void
  onChangeValue: (field: LineField, value: any) => void
  onChooseProduct: () => void
  onShowOriginal: () => void
  onClose: () => void
  onSave: (value: LineDraft) => void
}) {
  const [mode, setMode] = useState<'accept' | 'edit'>('accept')
  const [tab, setTab] = useState('details')
  const [unitFocusRequest, setUnitFocusRequest] = useState(0)
  const secondaryPanel = useRef<HTMLElement>(null)
  const initialMatch =
    line && draft
      ? matchFor(line, draft) || (line.candidates?.length === 1 ? line.candidates[0] : undefined)
      : undefined
  const needsConversion = !!(
    line &&
    draft &&
    !allowUnlinked && initialMatch?.unit &&
    !conversionFor(valuesFor(line, draft).unit, initialMatch, draft.unit_confirmation)
  )
  useEffect(() => {
    setMode(focusedField ? 'edit' : 'accept')
    setTab(focusedField === 'unit' || needsConversion ? 'units' : 'details')
  }, [line?.line_number, focusedField])
  useEffect(() => {
    if (needsConversion) setTab('units')
  }, [needsConversion])
  useEffect(() => {
    if (!unitFocusRequest || !line || !draft) return
    const frame = requestAnimationFrame(() => {
      const target = String(valuesFor(line, draft).unit || '').trim()
        ? secondaryPanel.current?.querySelector<HTMLElement>('#review-unit-factor')
        : window.document.getElementById('review-edit-unit')
      target?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
      target?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [unitFocusRequest, tab])
  if (!line || !draft) return null
  const values = valuesFor(line, draft)
  const match = matchFor(line, draft)
  const candidate = !match && line.candidates?.length === 1 ? line.candidates[0] : undefined
  const comparison = match || candidate
  const needsProduct = !allowUnlinked && !comparison
  const visibleNotes = [...new Set(line.issues.filter(issue => issue.field !== 'match').map(issue => issue.message))]
  const conversion = conversionFor(values.unit, comparison, draft.unit_confirmation)
  const missingUnit = !String(values.unit || '').trim()
  const openUnits = () => {
    setTab('units')
    if (missingUnit) setMode('edit')
    setUnitFocusRequest(value => value + 1)
  }
  const format = (field: LineField, value: any) =>
    ['price', 'amount', 'vat_amount', 'total'].includes(field)
      ? moneyValue(value)
      : field === 'quantity'
      ? displayValue(value).replace('.', ',')
      : displayValue(value)
  const split = (draft.allocations?.length || 0) > 1
  const differs = (field: LineField) => {
    if (split && ['quantity', 'price', 'amount'].includes(field)) return false
    if (comparison?.[field] == null) return false
    if (field === 'unit' && conversion) return false
    if (field === 'quantity' && conversion)
      return (
        Number(String(comparison[field]).replace(',', '.')) !==
        Number(String(values[field]).replace(',', '.')) * conversion.factor
      )
    return numbers.includes(field)
      ? Number(String(comparison[field]).replace(',', '.')) !==
          Number(String(values[field]).replace(',', '.'))
      : String(comparison[field]) !== String(values[field])
  }
  const editField = (field: LineField) =>
    numbers.includes(field) ? (
      <InputNumber
        id={`review-edit-${field}`}
        aria-label={lineReviewLabels[field]}
        stringMode
        decimalSeparator=","
        min="0"
        disabled={disabled}
        style={{ width: '100%' }}
        value={values[field] == null ? null : String(values[field])}
        onChange={value => onChangeValue(field, value)}
      />
    ) : (
      <Input
        id={`review-edit-${field}`}
        aria-label={lineReviewLabels[field]}
        disabled={disabled}
        value={String(values[field] ?? '')}
        onChange={event => onChangeValue(field, event.target.value)}
      />
    )
  const rounding = priceRounding(values.price, values.quantity, values.amount)
  const priceMismatch = !rounding.matches && Number.isFinite(rounding.difference)
  const ready = canConfirmLineDraft(line, draft, allowUnlinked) && !priceMismatch
  const quantityNumber = Number(String(values.quantity ?? '').replace(',', '.'))
  const amountNumber = Number(String(values.amount ?? '').replace(',', '.'))
  const recalculatedPrice = quantityNumber > 0 && Number.isFinite(amountNumber) && amountNumber >= 0
    ? Number((amountNumber / quantityNumber).toFixed(6)) : null
  const priceDelta = comparablePriceDifference(line, draft, amountBasis)
  return (
    <Modal
      className="supply-check__line-dialog"
      title={`Проверка строки ${line.line_number}`}
      open
      onCancel={onClose}
      width={1080}
      destroyOnHidden
      maskClosable={!disabled}
      keyboard={!disabled}
      footer={
        <div className="supply-check__line-footer">
          <div className="supply-check__line-footer-context">
          {needsConversion && <span className="supply-check__unit-required" role="status"><ExclamationCircleOutlined />{missingUnit ? 'Укажите единицу в накладной' : 'Нужно настроить пересчёт единиц'}</span>}
          {originalAction || (
            <Button type="link" icon={<FileSearchOutlined />} onClick={onShowOriginal}>
              Оригинал
            </Button>
          )}
          </div>
          <Space wrap>
            <Button disabled={disabled} onClick={onClose}>
              Отмена
            </Button>
            {needsProduct ? <Button type="primary" icon={<SearchOutlined />} disabled={disabled} onClick={onChooseProduct}>Выбрать товар в 1С</Button> : needsConversion ? <Button type="primary" icon={<SwapOutlined />} disabled={disabled} onClick={openUnits}>{missingUnit ? 'Указать единицу' : 'Настроить единицы'}</Button> : <Tooltip
              title={
                !ready
                  ? priceMismatch ? 'Исправьте цену или пересчитайте её по сумме строки' : 'Выберите товар, заполните значения и проверьте пересчёт единиц'
                  : undefined
              }
            >
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={disabled || !ready}
                onClick={() => onSave(confirmedLineDraft(line, draft, allowUnlinked))}
              >
                Подтвердить
              </Button>
            </Tooltip>}
          </Space>
        </div>
      }
    >
      <div className="supply-check__line-layout">
        <section className="supply-check__line-primary">
          <div className="supply-check__line-product">
            <strong>{displayValue(values.name)}</strong>
          </div>
          {!allowUnlinked && <section className={`supply-check__product-choice${comparison ? ' is-selected' : ''}`} aria-label="Соответствие товара в 1С">
            <div className="supply-check__product-choice-copy">
              <span className="supply-check__product-choice-label">{comparison ? match ? <><CheckCircleOutlined /> Товар в 1С выбран</> : <><SearchOutlined /> Предложен товар в 1С</> : <><SearchOutlined /> Товар в 1С не выбран</>}</span>
              {comparison ? <>
                <strong>{comparison.name}</strong>
                <small>{[comparison.article && `Артикул: ${comparison.article}`, comparison.unit && `Ед.: ${comparison.unit}`].filter(Boolean).join(' · ')}</small>
              </> : <small>Найдите соответствие по артикулу или названию.</small>}
            </div>
            <Button type={comparison ? 'default' : 'primary'} icon={<SearchOutlined />} disabled={disabled} onClick={onChooseProduct}>
              {comparison ? 'Заменить товар' : 'Выбрать товар в 1С'}
            </Button>
          </section>}
          {split && <Alert type="info" showIcon message="Строка распределена по нескольким основаниям" description={<Space direction="vertical" size={2}>{draft.allocations?.map((part, index) => <span key={index}>{part.match.dataset === 'orders' ? 'Заказ' : 'Заявка'} № {part.match.document_number} · {part.quantity} {values.unit}{part.unit_confirmation ? ` → ${(Number(part.quantity) * part.unit_confirmation.factor).toLocaleString('ru-RU', { maximumFractionDigits: 6 })} ${part.match.unit}` : ''}</span>)}</Space>} style={{ marginBottom: 12 }} />}
          <div className="supply-check__comparison-wrap">
            <table className="supply-check__comparison">
              <thead>
                <tr>
                  <th>Поле</th>
                  <th>В накладной</th>
                  <th>{candidate ? 'Вариант 1С' : 'В 1С'}</th>
                  <th aria-label="Сверка" />
                </tr>
              </thead>
              <tbody>
                {mainFields.map(field => (
                  <tr key={field} className={differs(field) ? 'has-difference' : undefined}>
                    <th scope="row">{lineReviewLabels[field]}</th>
                    <td>
                      {mode === 'edit' ? (
                        editField(field)
                      ) : (
                        <span>{format(field, values[field])}</span>
                      )}
                    </td>
                    <td>{split && ['quantity', 'price', 'amount'].includes(field) ? 'По основаниям' : format(field, comparison?.[field])}</td>
                    <td>
                      {comparison && !(split && ['quantity', 'price', 'amount'].includes(field)) && (
                        <Tooltip
                          title={differs(field) ? 'Значения отличаются' : 'Значения соответствуют'}
                        >
                          {differs(field) ? (
                            <span className="supply-check__comparison-difference">!</span>
                          ) : (
                            <CheckCircleOutlined className="supply-check__comparison-match" />
                          )}
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {visibleNotes.length > 0 && (
            <div className="supply-check__line-notes">
              {visibleNotes.map(text => <p key={text}>{text}</p>)}
            </div>
          )}
          {priceDelta != null && Number.isFinite(priceDelta) && Math.abs(priceDelta) > 0.005 && (
            <div className="supply-check__price-note">
              Цена в накладной {priceDelta > 0 ? 'выше' : 'ниже'} на{' '}
              {moneyValue(Math.abs(priceDelta))} за единицу. После подтверждения используем цену из накладной.
            </div>
          )}
          <Radio.Group
            className="supply-check__line-decision"
            value={mode}
            onChange={event => setMode(event.target.value)}
            disabled={disabled}
          >
            <Radio value="accept">Принять значения из накладной</Radio>
            <Radio value="edit">
              <EditOutlined /> Исправить значения
            </Radio>
          </Radio.Group>
          {rounding.matches && rounding.difference > 0.010001 && (
            <Alert
              type="info"
              showIcon
              message="Цена × количество и сумма строки отличаются в пределах округления. Итог из накладной сохраняется."
            />
          )}
          {priceMismatch && recalculatedPrice != null && (
            <Alert type="warning" showIcon style={{ marginTop: 12 }}
              message="Цена и количество не совпадают с суммой строки"
              description={<div>Можно сохранить сумму накладной и пересчитать цену за единицу до {recalculatedPrice.toLocaleString('ru-RU', { maximumFractionDigits: 6 })}. Исходная накладная не изменится.
                <div><Button size="small" type="primary" disabled={disabled} style={{ marginTop: 8 }} onClick={() => { setMode('edit'); onChangeValue('price', String(recalculatedPrice)) }}>Пересчитать цену по сумме</Button></div>
              </div>} />
          )}
          <MatchMemoryControls
            key={line.line_number}
            compact
            section="memory"
            line={line}
            draft={candidate ? { ...draft, match_index: 0 } : draft}
            onChange={onChange}
            disabled={disabled}
            hasDocumentContext={hasDocumentContext}
          />
        </section>
        <aside ref={secondaryPanel} className="supply-check__line-secondary">
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: 'details',
                label: (
                  <>
                    <FileTextOutlined /> Реквизиты
                  </>
                ),
                children: (
                  <Form layout="vertical">
                    <Form.Item label="Цена и сумма строки указаны">
                      <Select<NonNullable<LineDraft['amount_basis']>>
                        aria-label="НДС в цене и сумме строки"
                        value={draft.amount_basis ?? line.amount_basis ?? amountBasis ?? 'unknown'}
                        onChange={value =>
                          onChange({ ...draft, amount_basis: value, checked: false })
                        }
                        options={[
                          { value: 'unknown', label: 'Не определено' },
                          { value: 'net', label: 'Без НДС' },
                          { value: 'gross', label: 'С НДС' },
                        ]}
                      />
                    </Form.Item>
                    <div className="supply-check__edit-fields">
                      {(Object.keys(lineReviewLabels) as LineField[])
                        .filter(field => !mainFields.includes(field))
                        .map(field => (
                          <Form.Item
                            key={field}
                            label={lineReviewLabels[field]}
                            extra={
                              comparison?.[field] != null
                                ? `В 1С: ${format(field, comparison[field])}`
                                : undefined
                            }
                          >
                            {editField(field)}
                          </Form.Item>
                        ))}
                    </div>
                  </Form>
                ),
              },
              {
                key: 'units',
                label: (
                  <>
                    <SwapOutlined /> Единицы{' '}
                    {needsConversion && (
                      <span className="supply-check__unit-tab-required">Настроить</span>
                    )}
                  </>
                ),
                children: !comparison ? (
                  <div className="supply-check__units-placeholder">
                    <SwapOutlined />
                    <strong>Пересчёт единиц</strong>
                    <p>Единицы товара появятся здесь после его выбора в блоке слева.</p>
                  </div>
                ) : missingUnit ? <Alert type="warning" showIcon message="В накладной не указана единица измерения" description="Укажите её в таблице слева, затем настройте пересчёт." action={<Button size="small" disabled={disabled} onClick={openUnits}>Указать единицу</Button>} /> : (
                  <MatchMemoryControls
                    key={line.line_number}
                    section="units"
                    line={line}
                    draft={candidate ? { ...draft, match_index: 0 } : draft}
                    onChange={onChange}
                    disabled={disabled}
                    hasDocumentContext={hasDocumentContext}
                    onChooseProduct={onChooseProduct}
                  />
                ),
              },
              {
                key: 'extra',
                label: (
                  <>
                    <MoreOutlined /> Дополнительно
                  </>
                ),
                children: (
                  <>
                    <InvoiceFieldsEditor
                      title="Поля УПД"
                      presentation="plain"
                      hideEmpty
                      definitions={definitions.filter(field => !field.source_field)}
                      source={line}
                      draft={draft}
                      disabled={disabled}
                      onChange={field_values =>
                        onChange({ ...draft, field_values, checked: false })
                      }
                    />
                    <AdditionalFields
                      title="Дополнительные поля строки"
                      presentation="plain"
                      definitions={definitions}
                      fields={line.additional_fields}
                      values={draft.additional_values}
                      disabled={disabled}
                      onChange={additional_values =>
                        onChange({ ...draft, additional_values, checked: false })
                      }
                    />
                  </>
                ),
              },
            ]}
          />
        </aside>
      </div>
      {!ready && (
        <small className="supply-check__line-help">
          {!needsProduct && !needsConversion && 'Для подтверждения заполните обязательные значения. '}
          <Button
            type="link"
            size="small"
            disabled={disabled}
            onClick={() => onSave({ ...draft, checked: false })}
          >
            Сохранить правки без подтверждения
          </Button>
        </small>
      )}
    </Modal>
  )
}
